// ════════════════════════════════════════════════════════════════════════════
// WEB SEARCH — the pillar's RESULT SHAPE. The THIN case, and the one that
// shows the headline rule costs nothing where nothing folds.
//
// D21 already ruled that nothing folds onto a web-search headline: a result
// title is free text a stranger typed, and the pillar's own capture proves it
// twice over — of 24 captured titles one carries a bar outright (`CPOV | The
// ignored cabal`) and one an em dash, and a captured publisher label carries a
// middle dot (`Reddit · r/SonyHeadphones`). No divider survives that
// population.
//
// So this pillar's headline ALREADY carried exactly one datum: the title. The
// re-cut moves that one datum into a `title:` field and leaves the headline as
// a copy of it, DISPLAY ONLY. The rule is therefore uniform across all three
// pillars — the headline is never read — and the cost here is one field line
// per item. That is the floor of the adoption cost, and it is why the rule can
// be stated without exceptions: a pillar whose headline was already one value
// pays one line to become addressable.
//
// ─── CENSUS (D28), unchanged from `web-search.attach.ts` ────────────────────
//
// N = 24 rows over four lanes (serper.search 6, searchapi.googleSearch 6,
// hasdata.googleSerp 6, hasdata.googleSerpLight 6):
//   position 24/24 · title 24/24 · link 24/24 · snippet 24/24 ·
//   source 12/24 · date 13/24
// DRILL population ZERO — `#details` on a web-search row runs `page_read`'s
// own ladder and stores nothing, so no detail branch ships and the detail
// shape equals the lean shape.
//
// DELIBERATE OMISSIONS, unchanged: `position` (the block does not reproduce
// the result list, so a rank would claim a standing it cannot show — D22) and
// the row's `ref` (the row's address is printed as `link`; a `res-ref-` token
// beside it is a second handle for one thing — D6).
// ════════════════════════════════════════════════════════════════════════════

import type { AttachItemBlock, AttachFieldLine } from '../grammar.js';
import { attachFieldValue } from '../parse-attach-document.js';
import { attachTextOf } from './attach-value-codec.js';

/** WB's closed field vocabulary. Lean and detail are the same set today. */
export enum WebSearchAttachField {
  /** What the result calls itself, verbatim. Its own field rather than only
   *  the headline, so a widget reads a title instead of a display line. */
  Title = 'title',
  /** The page this result opens — the row's one DURABLE address (U3/R17,
   *  SPEC §5b): the writer's `/l/` citation short link where there is one,
   *  else the raw captured url. */
  Link = 'link',
  /** Who published it, as the answering lane labelled it: a display name on
   *  one lane and a bare host on another, verbatim either way. */
  Publisher = 'publisher',
  /** Google's own date on the result, verbatim — relative on some rows and
   *  absolute on others, and readable against the block's own capture line.
   *  Never parsed into an instant; the vendor sends a display string. */
  Date = 'date',
  /** What the result says it is about. Free text, its own line (D21). */
  Snippet = 'snippet',
}

/** One web result, typed. The object a widget renders from. */
export interface WebSearchAttachResult {
  readonly title: string;
  readonly linkUrl: string | null;
  readonly publisher: string | null;
  /** The vendor's display date string, verbatim. A string because that is what
   *  the wire holds — some lanes send `2 days ago`. */
  readonly dateText: string | null;
  readonly snippet: string | null;
}

/** The human read of one result — DISPLAY ONLY, and here it is the title
 *  itself, because nothing may fold beside it. Composed from the typed field,
 *  so the two cannot disagree. */
export function webSearchAttachHeadline(input: {
  result: WebSearchAttachResult;
}): string {
  return input.result.title;
}

function textLine(input: {
  key: WebSearchAttachField;
  value: string | null;
}): AttachFieldLine[] {
  const value = attachTextOf({ value: input.value });
  return value === null ? [] : [{ key: input.key, value }];
}

/** One typed result as a block item. */
export function writeWebSearchAttachItem(input: {
  result: WebSearchAttachResult;
}): AttachItemBlock {
  const result = input.result;
  return {
    headline: webSearchAttachHeadline({ result }),
    fields: [
      ...textLine({ key: WebSearchAttachField.Title, value: result.title }),
      ...textLine({ key: WebSearchAttachField.Link, value: result.linkUrl }),
      ...textLine({ key: WebSearchAttachField.Publisher, value: result.publisher }),
      ...textLine({ key: WebSearchAttachField.Date, value: result.dateText }),
      ...textLine({ key: WebSearchAttachField.Snippet, value: result.snippet }),
    ],
  };
}

function fieldText(input: {
  item: AttachItemBlock;
  key: WebSearchAttachField;
}): string | null {
  return attachTextOf({ value: attachFieldValue({ item: input.item, key: input.key }) });
}

/**
 * One block item, read as a typed result — or null when it carries no title.
 *
 * THE HEADLINE IS NOT READ, and this pillar is where that matters most: the
 * headline is free text a stranger typed, so a reader that took it as the
 * title would be reading a display line rather than a datum. It reads `title:`.
 */
export function readWebSearchAttachItem(input: {
  item: AttachItemBlock;
}): WebSearchAttachResult | null {
  const item = input.item;
  const title = fieldText({ item, key: WebSearchAttachField.Title });
  if (title === null) return null;
  return {
    title,
    linkUrl: fieldText({ item, key: WebSearchAttachField.Link }),
    publisher: fieldText({ item, key: WebSearchAttachField.Publisher }),
    dateText: fieldText({ item, key: WebSearchAttachField.Date }),
    snippet: fieldText({ item, key: WebSearchAttachField.Snippet }),
  };
}
